import React, { type FC, useCallback, useState } from "react";
import { reportError } from "@app/services/error-reporter";
import { MobileDialogFrame } from "@src/shared/ui/overlay/mobile-dialog-frame";
import { useDialogControls } from "@src/shared/ui/overlay/hooks/useDialogControls";
import type { ConfirmByComponentOptions, ConfirmOptions } from "./types";
import "./ConfirmOverlayContent.scss";

interface ConfirmOverlayContentProps {
    options: ConfirmOptions;
    onResolved: (value: boolean) => void;
}

function getComponentOptions(options: ConfirmOptions): ConfirmByComponentOptions | null {
    if ("content" in options && options.content != null) {
        return options;
    }
    return null;
}

/**
 * Контент confirm-dialog в overlay stack.
 */
export const ConfirmOverlayContent: FC<ConfirmOverlayContentProps> = ({ options, onResolved }) => {
    const dialogControls = useDialogControls();
    const [isLoading, setIsLoading] = useState(false);
    const componentOptions = getComponentOptions(options);

    const settle = useCallback(
        (value: boolean) => {
            onResolved(value);
            dialogControls.close();
        },
        [dialogControls, onResolved],
    );

    const handleConfirm = useCallback(() => {
        const onConfirm = componentOptions?.onConfirm;
        if (onConfirm != null) {
            setIsLoading(true);
            void (async () => {
                try {
                    await onConfirm();
                    settle(true);
                } catch (error) {
                    reportError(error, { from: "ConfirmOverlayContent" });
                } finally {
                    setIsLoading(false);
                }
            })();
            return;
        }

        settle(true);
    }, [componentOptions, settle]);

    const hasMessage = "message" in options && options.message != null;

    return (
        <MobileDialogFrame
            title={options.title ?? "Подтверждение"}
            confirmText={options.confirmText ?? "Подтвердить"}
            cancelText={options.cancelText ?? "Отмена"}
            confirmLoading={isLoading}
            onCancel={() => settle(false)}
            onConfirm={handleConfirm}
        >
            <div className="confirm_overlay_content">
                {hasMessage && <p className="confirm_overlay_content__message">{options.message}</p>}
                {componentOptions?.content}
            </div>
        </MobileDialogFrame>
    );
};
